import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";

/**
 * v0.4.73 — Three-mode theme provider.
 *
 * Mode: 'light' | 'dark' | 'system'
 *  - 'system' resolves at runtime via prefers-color-scheme.
 *  - Persisted to localStorage under VAT_THEME_KEY.
 *  - On mount the resolved theme is applied to <html> as the `dark`
 *    class (Tailwind `darkMode: 'class'` strategy).
 *  - When mode === 'system' we subscribe to the media query so the
 *    UI reflects OS changes without a reload.
 *
 * Sync-to-account is a future enhancement (operator pick Q4=a —
 * localStorage only for now).
 */
const VAT_THEME_KEY = "vat_theme";
const VALID_MODES = new Set(["light", "dark", "system"]);

const ThemeContext = createContext({
  mode: "system",
  resolved: "light",
  setMode: () => {},
  cycle: () => {},
});

const readStoredMode = () => {
  try {
    const v = localStorage.getItem(VAT_THEME_KEY);
    if (v && VALID_MODES.has(v)) return v;
  } catch {
    // localStorage blocked — fall through
  }
  return "system";
};

const systemPrefersDark = () => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
};

const apply = (resolved) => {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.dataset.theme = resolved;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeRaw] = useState(readStoredMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Subscribe to system pref so 'system' mode updates live.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const resolved = useMemo(() => {
    if (mode === "system") return systemDark ? "dark" : "light";
    return mode;
  }, [mode, systemDark]);

  // Apply on every resolved change.
  useEffect(() => { apply(resolved); }, [resolved]);

  const setMode = useCallback((next) => {
    if (!VALID_MODES.has(next)) return;
    setModeRaw(next);
    try { localStorage.setItem(VAT_THEME_KEY, next); } catch { /* ignore */ }
  }, []);

  // Three-mode cycle: light → dark → system → light …
  const cycle = useCallback(() => {
    setMode(mode === "light" ? "dark" : mode === "dark" ? "system" : "light");
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, resolved, setMode, cycle }),
    [mode, resolved, setMode, cycle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
